import React, { useRef, useState } from "react";
import { Label } from "../ui/label";
import { cn } from "../lib/utils";
import { useValidation } from "../DocumentView/ValidationContext";
import { useFieldMarks } from "../DocumentView/FieldMarksContext";

/**
 * Shared field-header parts so every element's title / subtitle / help text /
 * error list render identically. Change the style here once and it updates for
 * all elements. Layout (above vs. beside the control) stays with each element;
 * only the visual style of each part is centralised.
 */

/**
 * Bordered input "shell" for inputs with an addon (icon / prefix / stepper):
 * matches a plain input's border, radius, filled bg and focus ring but hosts a
 * borderless <input> plus the addon. Pair with FIELD_BARE_INPUT_CLASS. Add
 * `items-stretch` (cell addons: URL prefix, number stepper) or `items-center`
 * (inline icon: email) at the call site.
 */
export const FIELD_SHELL_CLASS =
  "flex overflow-hidden rounded-[var(--forms-input-radius)] border-[length:var(--forms-input-border-w)] border-[var(--forms-border)] bg-white transition-[border-color,box-shadow,background-color] [&:has(input[placeholder]:not(:placeholder-shown)):not(:focus-within)]:border-[var(--forms-input-filled-border)] [&:has(input[placeholder]:not(:placeholder-shown)):not(:focus-within)]:bg-[var(--forms-input-filled-bg)] focus-within:border-[var(--forms-accent,var(--forms-accent-default))] focus-within:shadow-[0_0_0_3px_var(--forms-accent-ring,var(--forms-accent-ring-default))]";

/** Borderless <input> that sits inside FIELD_SHELL_CLASS (add px per addon). */
export const FIELD_BARE_INPUT_CLASS =
  "min-w-0 flex-1 bg-transparent py-[var(--forms-input-py)] text-[length:var(--forms-input-font)] leading-[1.2] text-[var(--forms-ink)] placeholder:text-[var(--forms-faint)] focus:outline-none";

/** Filled state applied imperatively (native date/time can't be detected via
 *  CSS :placeholder-shown). Important so it wins over the shell's base bg. */
export const FIELD_FILLED_CLASS =
  "!border-[var(--forms-input-filled-border)] !bg-[var(--forms-input-filled-bg)]";

export const CalendarIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

export const ClockIcon: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

/**
 * A native date/time input dressed as a library field: leading icon (opens the
 * picker), borderless input inside the shell, and the filled bg only when it
 * holds a value and isn't focused. The browser's own picker indicator is hidden
 * so the leading icon is the single affordance.
 */
export const NativeInputField: React.FC<
  {
    icon: React.ReactNode;
    trailing?: React.ReactNode;
    shellClassName?: string;
  } & React.InputHTMLAttributes<HTMLInputElement>
> = ({ icon, trailing, shellClassName, className, ...inputProps }) => {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const filled = !!inputProps.value && !focused;
  const openPicker = () => {
    try {
      (ref.current as unknown as { showPicker?: () => void })?.showPicker?.();
    } catch {
      // showPicker throws if unsupported / not user-activated — ignore.
    }
  };
  return (
    <div
      className={cn(
        FIELD_SHELL_CLASS,
        "items-center gap-[10px] px-[13px]",
        filled && FIELD_FILLED_CLASS,
        shellClassName,
      )}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-label="Open picker"
        onClick={openPicker}
        className="flex flex-none items-center text-[var(--forms-optional)]"
      >
        {icon}
      </button>
      <input
        ref={ref}
        {...inputProps}
        onFocus={(e) => {
          setFocused(true);
          inputProps.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          inputProps.onBlur?.(e);
        }}
        className={cn(
          FIELD_BARE_INPUT_CLASS,
          // Uppercase the browser's format placeholder (dd/mm/yyyy → DD/MM/YYYY,
          // am/pm → AM/PM) and hide the native picker indicator/spinner.
          "uppercase [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-inner-spin-button]:hidden",
          // Empty → faint format text (like a placeholder); ink once filled.
          !inputProps.value && "text-[var(--forms-faint)]",
          className,
        )}
      />
      {trailing}
    </div>
  );
};

/**
 * Field title. Which marks it carries is the document's call over the
 * host's defaults (FieldMarksProvider): a filled form tags the few OPTIONAL
 * fields and leaves required ones bare, since the amber left rail already
 * says "required & to-do"; an element type's configuration form marks
 * nothing. The optional tag shows only when `required` is explicitly false.
 */
export const FieldLabel: React.FC<{
  htmlFor?: string;
  required?: boolean;
  className?: string;
  children: React.ReactNode;
}> = ({ htmlFor, required, className, children }) => {
  // A form-level "every question required" override makes every field
  // mandatory, so the OPTIONAL tag is suppressed even when the field's own
  // `required` config is false.
  const { requiredOverride } = useValidation();
  const { showRequiredMark, showOptionalMark } = useFieldMarks();
  const isRequired = required === true || requiredOverride;
  return (
    <Label
      htmlFor={htmlFor}
      className={cn(
        // Library spec: element name is 14px / 700, ink.
        "text-[length:var(--forms-label-font)] font-bold leading-none text-[var(--forms-ink)]",
        className,
      )}
    >
      {children}
      {showRequiredMark && isRequired && (
        <span className="ml-1 text-[var(--forms-required,#dc2626)]" aria-hidden="true">
          *
        </span>
      )}
      {showOptionalMark && required === false && !requiredOverride && (
        <span className="ml-2 text-[10.5px] font-bold uppercase tracking-[0.03em] text-[var(--forms-optional)]">
          Optional
        </span>
      )}
    </Label>
  );
};

/** Field subtitle (a.k.a. sub-label / description) shown under the title. */
export const FieldSubLabel: React.FC<{
  className?: string;
  children?: React.ReactNode;
}> = ({ className, children }) =>
  children ? (
    <p className={cn("text-[12.5px] text-[var(--forms-muted)]", className)}>
      {children}
    </p>
  ) : null;

/** Field help text — smaller muted note, shown below the control. */
export const FieldHelpText: React.FC<{
  className?: string;
  children?: React.ReactNode;
}> = ({ className, children }) =>
  children ? (
    <p className={cn("text-[12px] text-[var(--forms-muted-soft)]", className)}>
      {children}
    </p>
  ) : null;

/** Validation error list. */
export const FieldErrors: React.FC<{ errors?: string[]; className?: string }> = ({
  errors,
  className,
}) =>
  errors && errors.length > 0 ? (
    <ul className={cn("space-y-0.5 text-xs text-destructive", className)}>
      {errors.map((e) => (
        <li key={e}>{e}</li>
      ))}
    </ul>
  ) : null;

/**
 * The common title + subtitle block (title above the control, subtitle under
 * it). Used by the majority of elements; inline-label elements (toggle,
 * checkbox) compose FieldLabel / FieldSubLabel directly instead.
 */
export const FieldHeader: React.FC<{
  htmlFor?: string;
  label: React.ReactNode;
  subLabel?: React.ReactNode;
  required?: boolean;
  className?: string;
  labelClassName?: string;
}> = ({ htmlFor, label, subLabel, required, className, labelClassName }) => (
  <div className={cn("space-y-1", className)}>
    <FieldLabel htmlFor={htmlFor} required={required} className={labelClassName}>
      {label}
    </FieldLabel>
    <FieldSubLabel>{subLabel}</FieldSubLabel>
  </div>
);
